<template>
  <!-- driver.js injects outside the DOM tree; wrapper is layout-neutral -->
  <div class="contents">
    <span class="sr-only" aria-hidden="true" />
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, watch, type WatchStopHandle } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { driver, type Config, type DriveStep, type Driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { useCpAddonToursStore } from '@/stores/cp-addon-tours'
import { storeToRefs } from 'pinia'
import {
  attachAddonTourSkipRow,
  addonTourProgressText,
  scheduleDriverLayoutSync,
} from '@/utils/cp-addon-tour-driver'
import {
  areTourAutoStartsDisabled,
  disableAllTourAutoStarts,
  markTourSeen,
  VIEW_TOUR_DISABLE_AUTO_START_KEY,
} from '@/utils/staff-tour-state'

const props = defineProps<{
  addonCode: string
  /** When true the tour starts immediately on mount without waiting for a store signal. */
  autoStart?: boolean
}>()

const emit = defineEmits<{
  (e: 'tour-started'): void
  (e: 'tour-ended'): void
}>()

const { t, te } = useI18n()
const router = useRouter()
const toursStore = useCpAddonToursStore()
const { restartSignals, actionResult } = storeToRefs(toursStore)

let driverInstance: Driver | null = null
/** Prevents tour-ended from being emitted more than once per tour lifecycle. */
let tourEndedEmitted = false
/** Cleanup handle for the action-result watcher set during a triggerAction pause. */
let actionResultUnwatch: WatchStopHandle | null = null
/**
 * Carries the action result data (planId, routeName, …) across all phases after it
 * is first set by a triggerAction. This allows later phases to use the navigation
 * context without requiring additional store round-trips.
 */
let persistedActionData: Record<string, unknown> = {}
const tourKey = `cp-addon:${props.addonCode}:v1`
const tourVersion = 1

// ─── Step definitions ──────────────────────────────────────────────────────

/**
 * A step anchor definition.
 * - `route`: navigate here before this phase starts (used for phase-1 first step).
 * - `triggerAction`: when the user clicks Next on this step, pause the tour and fire
 *   a store action instead of moving to the next step. The tour resumes once the view
 *   calls confirmTourAction() with result data.
 */
type TourStepDef = {
  anchor: string
  route?: string
  triggerAction?: string
}

/**
 * Multi-phase steps: each add-on code maps to an array of phases.
 * Most add-ons have a single phase. seating_pro has two (list view + detail view).
 * The first step of each phase may carry a `route` for pre-navigation.
 */
const TOUR_PHASES_BY_CODE: Record<string, TourStepDef[][]> = {
  seating_pro: [
    // Phase 0: List view — ends with animated modal create; view confirms with planId + routeName
    [
      { anchor: 'seating-pro-sidebar-link', route: '/catalog/seating' },
      { anchor: 'seating-pro-plans-list' },
      { anchor: 'seating-pro-create-btn', triggerAction: 'create-demo-plan' },
    ],
    // Phase 1: Still on list view — highlight the newly created (or existing) demo row.
    // Navigation to the detail view happens at the start of phase 2 via persistedActionData.
    [
      { anchor: 'seating-pro-demo-row', triggerAction: 'navigate-to-demo-plan' },
    ],
    // Phase 2: Detail view (overview tab) — entered via persisted routeName + planId
    [
      { anchor: 'seating-plan-name-field' },
      { anchor: 'seating-plan-layout-tab', triggerAction: 'switch-to-layout-tab' },
    ],
    // Phase 3: Detail view (layout tab) — upload area is now visible
    [
      { anchor: 'seating-plan-upload-area' },
    ],
  ],
  gift_cards: [
    [
      { anchor: 'gift-cards-sidebar-link', route: '/finance/gift-card-products' },
      { anchor: 'gift-cards-list' },
      { anchor: 'gift-cards-create-btn' },
    ],
  ],
  surveys: [
    [
      { anchor: 'surveys-sidebar-link' },
      { anchor: 'surveys-list' },
    ],
  ],
  memberships: [
    [
      { anchor: 'memberships-sidebar-link' },
      { anchor: 'memberships-list' },
    ],
  ],
  waitlist_automation: [
    [
      { anchor: 'waitlist-sidebar-link' },
      { anchor: 'waitlist-list' },
    ],
  ],
  integrations_plus: [
    [
      { anchor: 'integrations-sidebar-link', route: '/settings' },
      { anchor: 'integrations-api-section' },
    ],
  ],
  sponsor_placements: [
    [
      { anchor: 'sponsor-placements-sidebar-link', route: '/webshop' },
      { anchor: 'sponsor-placements-section' },
    ],
  ],
  corporate_billing: [
    [
      { anchor: 'corporate-billing-sidebar-link', route: '/settings' },
      { anchor: 'corporate-billing-section' },
    ],
  ],
  social: [
    [
      { anchor: 'social-sidebar-link' },
      { anchor: 'social-section' },
    ],
  ],
  ticket_buddy_connect: [
    [
      { anchor: 'connect-sidebar-link', route: '/settings' },
      { anchor: 'connect-api-section' },
    ],
  ],
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function stepI18nKey(addonCode: string, anchor: string, part: 'title' | 'description'): string {
  return `cpPackages.tour.steps.${addonCode}.${anchor}.${part}`
}

function resolveElement(anchor: string): HTMLElement {
  return (document.querySelector(`[data-cp-tour="${anchor}"]`) as HTMLElement | null) ?? document.body
}

function buildSteps(defs: TourStepDef[]): DriveStep[] {
  return defs
    .filter((def) => document.querySelector(`[data-cp-tour="${def.anchor}"]`))
    .map((def) => {
      const titleKey = stepI18nKey(props.addonCode, def.anchor, 'title')
      const descKey = stepI18nKey(props.addonCode, def.anchor, 'description')
      return {
        element: () => resolveElement(def.anchor),
        popover: {
          title: te(titleKey) ? t(titleKey) : '',
          description: te(descKey) ? t(descKey) : '',
          side: 'bottom' as const,
        },
      }
    })
    .filter((step) => step.popover.title || step.popover.description)
}

function prefersReducedMotion(): boolean {
  return typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
}

function emitTourEnded(): void {
  if (tourEndedEmitted) return
  tourEndedEmitted = true
  emit('tour-ended')
}

function destroyInstance(): void {
  driverInstance?.destroy()
  driverInstance = null
}

function clearActionResultWatcher(): void {
  actionResultUnwatch?.()
  actionResultUnwatch = null
}

// ─── Driver factory ────────────────────────────────────────────────────────

/**
 * Returns the step definitions array for the current phase, indexed by the
 * driver's current active step index. Used to resolve triggerAction on Next.
 */
function getCurrentPhaseDefs(phaseIndex: number): TourStepDef[] {
  return TOUR_PHASES_BY_CODE[props.addonCode]?.[phaseIndex] ?? []
}

function createDriver(phaseDefs: TourStepDef[], phaseIndex: number): Driver {
  const allPhases = TOUR_PHASES_BY_CODE[props.addonCode] ?? []
  const isLastPhase = phaseIndex === allPhases.length - 1

  // For non-final phases driver.js shows doneBtnText on the last step of that phase
  // (because it considers it the last step). Use nextBtnText so the button looks
  // consistent — onNextClick intercepts it anyway and continues to the next phase.
  const nextLabel = t('cpPackages.tour.next')
  const doneLabel = isLastPhase ? t('cpPackages.tour.done') : nextLabel

  const cfg: Config = {
    showProgress: true,
    progressText: addonTourProgressText(t),
    nextBtnText: nextLabel,
    prevBtnText: t('cpPackages.tour.previous'),
    doneBtnText: doneLabel,
    showButtons: ['next', 'previous', 'close'],
    animate: !prefersReducedMotion(),
    overlayOpacity: 0.75,
    overlayColor: '#000000',
    smoothScroll: false,
    allowClose: true,
    overlayClickBehavior: () => {},
    stagePadding: 8,
    stageRadius: 10,
    popoverClass: 'cp-addon-tour-popover',
    onHighlighted: (_element, _step, { driver: d }) => {
      scheduleDriverLayoutSync(d)
    },
    onPopoverRender: (popover, { driver: d }) => {
      attachAddonTourSkipRow(popover, d, t('cpPackages.tour.skipTour'), () => {
        void markTourSeen(tourKey, tourVersion, 'dismissed')
        clearActionResultWatcher()
        destroyInstance()
        emitTourEnded()
      }, t('viewTour.disableAutoStarts'), () => {
        void disableAllTourAutoStarts(tourKey, tourVersion)
        try {
          window.localStorage.setItem(VIEW_TOUR_DISABLE_AUTO_START_KEY, '1')
        } catch {
          // A blocked localStorage must not block closing the tour.
        }
        clearActionResultWatcher()
        destroyInstance()
        emitTourEnded()
      })
    },
    onNextClick: (_element, _step, { driver: d }) => {
      const currentStepIndex = d.getActiveIndex() ?? 0
      const currentDef = getCurrentPhaseDefs(phaseIndex)[currentStepIndex]

      if (currentDef?.triggerAction) {
        // Self-confirmed actions need no external view handler — the tour resolves them
        // directly using already-persisted data (e.g. navigate-to-demo-plan).
        if (currentDef.triggerAction === 'navigate-to-demo-plan') {
          void continueAfterAction({}, phaseIndex)
          return
        }

        // All other actions: pause and delegate to the view via the store.
        // The actionResult watcher will call continueAfterAction once confirmed.
        toursStore.requestTourAction(props.addonCode, currentDef.triggerAction)
        actionResultUnwatch = watch(
          actionResult,
          (result) => {
            if (result?.addonCode !== props.addonCode) return
            clearActionResultWatcher()
            void continueAfterAction(result.data, phaseIndex)
          },
          { deep: true },
        )
        return
      }

      if (d.isLastStep()) {
        void markTourSeen(tourKey, tourVersion, 'completed')
        destroyInstance()
        emitTourEnded()
        return
      }
      d.moveNext()
    },
    onPrevClick: (_element, _step, { driver: d }) => {
      d.movePrevious()
    },
    onCloseClick: (_element, _step, { driver: d }) => {
      clearActionResultWatcher()
      d.destroy()
      driverInstance = null
      emitTourEnded()
    },
    onDestroyed: () => {
      driverInstance = null
      emitTourEnded()
    },
  }

  const d = driver(cfg)
  d.setSteps(buildSteps(phaseDefs))
  return d
}

// ─── Phase continuation ────────────────────────────────────────────────────

/**
 * Called after a triggerAction is confirmed by the view (or self-confirmed for
 * navigate-to-demo-plan which needs no external view handler).
 *
 * Action data semantics:
 * - `planId: number`  + `routeName: string` → navigate to detail view, then continue
 * - `planId: null`                           → view reported failure, end tour gracefully
 * - empty data `{}`                          → in-place action (tab switch etc.), continue
 *
 * `persistedActionData` carries planId/routeName from phase 0 through later phases
 * so that phase 1 (demo-row highlight, no navigation) and phase 2 (detail view entry)
 * can both access the originally created plan's coordinates.
 */
async function continueAfterAction(
  data: Record<string, unknown>,
  completedPhaseIndex: number,
): Promise<void> {
  const nextPhaseIndex = completedPhaseIndex + 1
  const allPhases = TOUR_PHASES_BY_CODE[props.addonCode] ?? []
  const nextPhaseDefs = allPhases[nextPhaseIndex]

  destroyInstance()

  if (!nextPhaseDefs) {
    emitTourEnded()
    return
  }

  // Persist the first non-empty action result so later phases can reuse planId/routeName.
  if (Object.keys(data).length > 0) {
    persistedActionData = { ...persistedActionData, ...data }
  }

  const routeName = (data.routeName ?? persistedActionData.routeName) as string | undefined
  const planId = (data.planId ?? persistedActionData.planId) as number | null | undefined

  // Navigation action failure: planId=null means the view reported an error.
  if (routeName !== undefined && planId === null) {
    emitTourEnded()
    return
  }

  // Phase 1 (demo-row) stays on the current route — no navigation.
  // Phase 2+ navigates to the detail view using the persisted coordinates.
  const nextFirstDef = nextPhaseDefs[0]
  const isNavigationPhase =
    nextFirstDef?.anchor !== 'seating-pro-demo-row' && routeName && planId

  if (isNavigationPhase) {
    // Navigate to the overview tab explicitly so anchors in that tab are in the DOM.
    // The detail view defaults to the layout tab, which would hide overview anchors.
    await router.push({
      name: routeName as string,
      params: { planId: String(planId) },
      query: { tab: 'overview' },
    })
  }

  // Wait for the new view and its anchors to appear in the DOM.
  await waitForAnchors(nextPhaseDefs)

  const steps = buildSteps(nextPhaseDefs)
  if (steps.length === 0) {
    emitTourEnded()
    return
  }

  driverInstance = createDriver(nextPhaseDefs, nextPhaseIndex)
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const d = driverInstance
      d?.drive(0)
      if (d?.isActive()) scheduleDriverLayoutSync(d)
    })
  })
}

// ─── DOM readiness ─────────────────────────────────────────────────────────

/**
 * Waits until all required anchors for the given step definitions are present
 * in the DOM, or until the timeout expires.
 *
 * After a router.push() the target view needs to mount AND finish its initial
 * data fetch before it renders the anchored elements. A fixed rAF count is not
 * reliable; polling with a timeout is the practical solution here.
 */
/**
 * Waits until at least one non-triggerAction anchor from the given phase defs is
 * present in the DOM, or until timeoutMs elapses.
 *
 * Uses a MutationObserver for immediate detection once nodes are inserted, with a
 * rAF-based poll as the initial kick and a hard timeout as safety net.
 * This is significantly faster than a pure rAF poll after router.push().
 */
function waitForAnchors(defs: TourStepDef[], timeoutMs = 4000): Promise<void> {
  return new Promise<void>((resolve) => {
    const anchors = defs.filter((d) => !d.triggerAction).map((d) => d.anchor)

    if (anchors.length === 0) {
      resolve()
      return
    }

    const isReady = () =>
      anchors.some((a) => document.querySelector(`[data-cp-tour="${a}"]`) !== null)

    if (isReady()) {
      resolve()
      return
    }

    let settled = false
    const done = () => {
      if (settled) return
      settled = true
      observer.disconnect()
      clearTimeout(timer)
      resolve()
    }

    const observer = new MutationObserver(() => {
      if (isReady()) done()
    })
    observer.observe(document.body, { childList: true, subtree: true, attributes: true })

    const timer = setTimeout(done, timeoutMs)
  })
}

// ─── Tour start ────────────────────────────────────────────────────────────

/** Navigates to the first phase's route if not already there, then starts phase 0. */
async function startTour(): Promise<void> {
  if (driverInstance?.isActive()) return

  const allPhases = TOUR_PHASES_BY_CODE[props.addonCode] ?? []
  const phase0Defs = allPhases[0] ?? []
  const firstRoute = phase0Defs[0]?.route

  if (firstRoute && router.currentRoute.value.path !== firstRoute) {
    await router.push(firstRoute)
  }

  // Wait for the view to mount and render its anchored elements before measuring.
  await waitForAnchors(phase0Defs)

  const steps = buildSteps(phase0Defs)
  if (steps.length === 0) {
    emit('tour-ended')
    return
  }

  tourEndedEmitted = false
  persistedActionData = {}
  driverInstance = createDriver(phase0Defs, 0)
  emit('tour-started')
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      const d = driverInstance
      d?.drive(0)
      if (d?.isActive()) scheduleDriverLayoutSync(d)
    })
  })
}

// ─── Lifecycle ─────────────────────────────────────────────────────────────

onMounted(() => {
  if (props.autoStart) {
    void (async () => {
      try {
        if (window.localStorage.getItem(VIEW_TOUR_DISABLE_AUTO_START_KEY) === '1') {
          await disableAllTourAutoStarts(tourKey, tourVersion)
        }
      } catch {
        // Continue without persistence when localStorage is unavailable.
      }
      if (areTourAutoStartsDisabled()) {
        emitTourEnded()
        return
      }
      void startTour()
    })()
  }
})

watch(
  () => restartSignals.value[props.addonCode],
  (signal) => {
    // Only react to signal changes after mount; autoStart handles the initial start.
    if (signal == null || signal === 0) return
    clearActionResultWatcher()
    destroyInstance()
    void startTour()
  },
)

defineExpose({ startTour })

onUnmounted(() => {
  clearActionResultWatcher()
  destroyInstance()
})
</script>

<style>
/*
 * Tour popover styles live in main.css under the `.cp-addon-tour-popover` selector.
 * Scoped styles cannot reach driver.js-injected DOM outside the component root.
 */
</style>
